// #130/#141 Sevkiyat Planlama tahtası — Sipariş Planla & Dönüştür.
// Düzen (#141): kriter çubuğu (3 segment) → aksiyon satırı → "Planlanan Araç" seçim çubuğu
// → 2 panel (Bekleyen Siparişler | seçili Araç Detayı [Yüklenen Siparişler / Rota Detayı])
// → seçili sipariş detay paneli. "Planlanmadı" havuzu kaldırıldı: araca yüklenmemiş = bekleyen.
//
// State modeli:
//   vehicles[]  : her biri { ..., orders[] }
//   bekleyen[]  : araca atanmamış (planlanmamış) tüm siparişler
//   selectedVehicleId / selectedOrderId
//   adjustments : Map(orderDetailId -> { orderId, newQty })  (stok kontrolünden)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import CriteriaBar from './shipmentPlanning/CriteriaBar';
import PendingPanel from './shipmentPlanning/PendingPanel';
import VehicleSelectBar from './shipmentPlanning/VehicleSelectBar';
import VehicleDetailPanel from './shipmentPlanning/VehicleDetailPanel';
import OrderDetailPanel from './shipmentPlanning/OrderDetailPanel';
import StockControlLayer from './shipmentPlanning/StockControlLayer';
import OptimizeModal from './shipmentPlanning/OptimizeModal';
import {
  API_BASE, unwrap, apiMessage, pick, todayPlus, fromDateInput, toDayEndInput, fmtNumber,
  normalizeOrder, normalizeVehicle, planStatusMeta, vehicleTotals,
} from './shipmentPlanning/shipmentPlanningUtils';

const DEFAULT_CRITERIA = () => ({
  vendorId: null, vendorName: '',
  warehouseId: null, warehouseName: '',
  vehicleIds: [],
  orderDateFrom: todayPlus(-5), orderDateTo: todayPlus(1),
  shipDateFrom: '', shipDateTo: '',
  customerGroupIds: [], subGroupIds: [], dynamicGroupIds: [], salesRepIds: [],
  additionalFields: [], shipmentArea: { polygons: [] },
});

const vehicleKeyOf = (v) => v.id || v.shipmentVehicleId;

export function ShipmentPlanningBoardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  usePageMeta('Sipariş Planla & Dönüştür', 'Bekleyen siparişleri araçlara dağıtın, rotalayın, belgeye dönüştürün.');
  // Menü aksiyon hakları: yeni plan Ekleme, mevcut plan Güncelleme hakkı ister.
  // Yalnız sunucuya yazan aksiyonlar (Taslak Kaydet / Belge Dönüştür) kapılanır;
  // listeleme, sürükleme, stok kontrol ve optimize yalnız yerel durumu değiştirir.
  const { canAdd, canEdit } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  const [criteria, setCriteria] = useState(DEFAULT_CRITERIA);
  const [vehicles, setVehicles] = useState([]);
  const [bekleyen, setBekleyen] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [adjustments, setAdjustments] = useState({}); // orderDetailId -> { orderId, newQty }
  const [vehicleKm, setVehicleKm] = useState({});      // vehicleId -> km

  const [planId, setPlanId] = useState(isNew ? null : id);
  const [planNo, setPlanNo] = useState('');
  const [status, setStatus] = useState(isNew ? null : 'DRAFT');

  const [listing, setListing] = useState(false);
  // #152 madde 5/11 — Listele'ye basılana kadar ekranın alt kısmı (araçlar + paneller) BOŞ gelir.
  const [listed, setListed] = useState(!isNew);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  // Sürüklenen sipariş(ler). Çoklu seçim varsa ve tutulan kart seçiliyse tüm seçim taşınır.
  const [dragOrders, setDragOrders] = useState([]);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [stockOpen, setStockOpen] = useState(false);
  const [optimizeFor, setOptimizeFor] = useState(null);
  const [convertConfirm, setConvertConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 4000); };

  // ── Var olan planı yükle ──────────────────────────────────────────────────
  useEffect(() => {
    if (isNew) return;
    let alive = true;
    setLoading(true);
    fetch(`${API_BASE}/GetById?id=${encodeURIComponent(id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (!alive) return;
        const data = unwrap(d) || {};
        const plan = data.plan || data.Plan || {};
        setPlanNo(pick(plan, 'planNo', 'PlanNo', 'textId', 'TextId') || '');
        setStatus(pick(plan, 'status', 'Status') || 'DRAFT');
        setCriteria((prev) => ({
          ...prev,
          vendorId: pick(plan, 'vendorId', 'VendorId') || prev.vendorId,
          vendorName: pick(plan, 'vendorName', 'VendorName') || prev.vendorName,
        }));
        const vs = (data.vehicles || data.Vehicles || []).map((v) => {
          const nv = normalizeVehicle(v);
          nv.orders = (v.orders || v.Orders || []).map(normalizeOrder);
          return nv;
        });
        setVehicles(vs);
        // Bekleyen + (eski) planlanmadı kayıtları artık tek "bekleyen" havuzunda toplanır.
        const pendingRows = (data.pendingOrders || data.PendingOrders || []).map(normalizeOrder);
        const unplannedRows = (data.unplannedOrders || data.UnplannedOrders || []).map(normalizeOrder);
        setBekleyen([...pendingRows, ...unplannedRows]);
        if (vs.length) setSelectedVehicleId(vehicleKeyOf(vs[0]));
      })
      .catch(() => { if (alive) showToast('error', 'Plan yüklenemedi.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // ── LİSTELE — kriterlere göre bekleyen siparişleri + araçları getir ────────
  const handleList = async () => {
    // #152 madde 11 — Distribütör seçilmeden listeleme yapılamaz.
    if (!criteria.vendorId) { showToast('error', 'Önce Distribütör seçin.'); return; }
    setListing(true);
    try {
      const body = {
        vendorId: criteria.vendorId || null,
        warehouseId: criteria.warehouseId || null,
        vehicleIds: criteria.vehicleIds || [],
        orderDateFrom: fromDateInput(criteria.orderDateFrom),
        // #807 Bitiş tarihi DAHİL olmalı: gün başı gönderilince o günün belgeleri eleniyordu.
        // ShipDate şu an backend'de FİLTRE DEĞİL (OrderHeader'da ShipDate yok, bkz.
        // ShipmentPlansController "ShipDate filtresi atlanır" notu) — filtreye girdiği gün
        // üst sınırı burada da toDayEndInput'a çevirin.
        orderDateTo: toDayEndInput(criteria.orderDateTo),
        shipDateFrom: fromDateInput(criteria.shipDateFrom),
        shipDateTo: fromDateInput(criteria.shipDateTo),
        customerGroupIds: criteria.customerGroupIds || [],
        subGroupIds: criteria.subGroupIds || [],
        dynamicGroupIds: criteria.dynamicGroupIds || [],
        salesRepIds: criteria.salesRepIds || [],
        additionalFields: (criteria.additionalFields || []).map((f) => ({ fieldId: f.fieldId, value: f.value })),
        shipmentArea: { polygons: criteria.shipmentArea?.polygons || [] },
      };
      const res = await fetch(`${API_BASE}/pending-orders`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json();
      if (!res.ok) throw new Error(apiMessage(payload, 'Siparişler getirilemedi'));
      const data = unwrap(payload) || {};
      const vs = (data.vehicles || []).map(normalizeVehicle);
      const orders = (data.orders || []).map(normalizeOrder);

      // Oto-dağıt: assignedVehicleId olan araca yerleşir; gerisi Bekleyen'de.
      const byVehicle = new Map(vs.map((v) => [String(v.shipmentVehicleId), v]));
      const pending = [];
      const visit = {};
      orders.forEach((o) => {
        const key = o.assignedVehicleId != null ? String(o.assignedVehicleId) : null;
        if (key && byVehicle.has(key)) {
          const v = byVehicle.get(key);
          visit[key] = (visit[key] || 0) + 1;
          v.orders.push({ ...o, visitOrderNo: o.visitOrderNo ?? visit[key] });
        } else {
          pending.push(o);
        }
      });
      setVehicles(vs);
      setBekleyen(pending);
      setAdjustments({});
      setCheckedIds(new Set());
      setSelectedVehicleId(vs.length ? vehicleKeyOf(vs[0]) : null);
      setSelectedOrderId(null);
      setListed(true);
      showToast('success', `${orders.length} sipariş, ${vs.length} araç listelendi.`);
    } catch (e) {
      showToast('error', e.message || 'Siparişler getirilemedi');
    } finally {
      setListing(false);
    }
  };

  // En güncel ikiliyi senkron okuyabilmek için ref'ler.
  const vehiclesRef = useRef(vehicles);
  const bekleyenRef = useRef(bekleyen);
  useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);
  useEffect(() => { bekleyenRef.current = bekleyen; }, [bekleyen]);

  // ── Tüm araçların KM'sini canlı tut ───────────────────────────────────────
  const kmSigRef = useRef({});
  useEffect(() => {
    let alive = true;
    vehicles.forEach((v) => {
      const key = vehicleKeyOf(v);
      const pts = (v.orders || [])
        .slice()
        .sort((a, b) => (a.visitOrderNo ?? 9999) - (b.visitOrderNo ?? 9999))
        .map((o) => ({ lat: o.customerLat, lng: o.customerLng }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      const sig = pts.map((p) => `${p.lat},${p.lng}`).join('|');
      if (kmSigRef.current[key] === sig) return;
      kmSigRef.current[key] = sig;
      if (pts.length < 2) { setVehicleKm((prev) => ({ ...prev, [key]: 0 })); return; }
      fetch(`${API_BASE}/route`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ points: pts }) })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => { if (alive) setVehicleKm((prev) => ({ ...prev, [key]: Number(unwrap(d)?.km) || 0 })); })
        .catch(() => {});
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const findOrder = (orderId) => {
    for (const v of vehicles) { const f = v.orders.find((o) => o.orderId === orderId); if (f) return f; }
    return bekleyen.find((o) => o.orderId === orderId);
  };

  // Sipariş(leri) her yerden çıkarır, hedefe ekler; iki state'i birlikte günceller.
  // Araca eklerken dağıtım sırası (visitOrderNo) mevcut en büyükten devam eder.
  const moveOrdersTo = useCallback((orders, target) => {
    const list = (orders || []).filter(Boolean);
    if (!list.length) return;
    const movingIds = new Set(list.map((o) => o.orderId));

    let vs = vehiclesRef.current.map((v) => ({ ...v, orders: v.orders.filter((o) => !movingIds.has(o.orderId)) }));
    let bek = bekleyenRef.current.filter((o) => !movingIds.has(o.orderId));

    if (target.type === 'vehicle') {
      vs = vs.map((v) => {
        if (String(vehicleKeyOf(v)) !== String(target.vehicleId)) return v;
        let seq = v.orders.reduce((m, o) => Math.max(m, o.visitOrderNo || 0), 0);
        const added = list.map((o) => ({ ...o, visitOrderNo: ++seq }));
        return { ...v, orders: [...v.orders, ...added] };
      });
    } else { // bekleyen
      bek = [...bek, ...list.map((o) => ({ ...o, visitOrderNo: null }))];
    }

    vehiclesRef.current = vs; bekleyenRef.current = bek;
    setVehicles(vs); setBekleyen(bek);
    setCheckedIds(new Set());
  }, []);

  const moveOrderTo = useCallback((order, target) => moveOrdersTo([order], target), [moveOrdersTo]);

  // Sürükleme başlangıcı: tutulan kart seçili kümedeyse tüm seçim taşınır, değilse yalnız o kart.
  const beginDrag = (order) => {
    if (!order) return;
    setDragOrders(checkedIds.has(order.orderId) && checkedIds.size > 1
      ? [...checkedIds].map(findOrder).filter(Boolean)
      : [order]);
  };
  const endDrag = () => setDragOrders([]);

  const onDropToVehicle = (vehicleId) => { moveOrdersTo(dragOrders, { type: 'vehicle', vehicleId }); setDragOrders([]); };
  const onDropToBekleyen = () => { moveOrdersTo(dragOrders, { type: 'bekleyen' }); setDragOrders([]); };
  const removeFromVehicle = (order) => moveOrderTo(order, { type: 'bekleyen' });

  // ── Çoklu seçim ────────────────────────────────────────────────────────────
  const toggleChecked = (order) => setCheckedIds((prev) => {
    const next = new Set(prev);
    if (next.has(order.orderId)) next.delete(order.orderId); else next.add(order.orderId);
    return next;
  });
  // Bir listedeki (bekleyen ya da araç) siparişlerin tamamını seç / seçimi kaldır.
  const setCheckedForList = (orders, checked) => setCheckedIds((prev) => {
    const next = new Set(prev);
    orders.forEach((o) => (checked ? next.add(o.orderId) : next.delete(o.orderId)));
    return next;
  });

  // ── Belge türü değişimi (her yerde) ───────────────────────────────────────
  const changeDocType = (order, newType) => {
    const apply = (o) => o.orderId === order.orderId ? { ...o, documentType: newType } : o;
    setVehicles((v) => v.map((veh) => ({ ...veh, orders: veh.orders.map(apply) })));
    setBekleyen((b) => b.map(apply));
  };

  // ── Distribütör (Vendor) gösterimi (araç bazında) ───────────────────────
  // #214 Araç başlığındaki alan artık Distribütör (Vendor) listesini gösterir; plandaki tüm
  // araçlar aynı kanala aittir, seçim yalnız görsel/istemci-durumudur (kanal kriterle sabittir).
  const changeVehicleVendor = (vehicle, vendorId, vendorName) => {
    setVehicles((v) => v.map((veh) => vehicleKeyOf(veh) === vehicleKeyOf(vehicle) ? { ...veh, vendorId, vendorName } : veh));
  };

  // ── Stok kontrol ayarlamaları ──────────────────────────────────────────────
  const allPlannedOrderIds = useMemo(
    () => vehicles.flatMap((v) => v.orders.map((o) => o.orderId)),
    [vehicles],
  );
  const applyAdjustments = (list) => {
    setAdjustments((prev) => {
      const next = { ...prev };
      list.forEach((a) => { next[a.orderDetailId] = { orderId: a.orderId, newQty: a.newQty }; });
      return next;
    });
    const affected = new Set(list.map((a) => a.orderId));
    setVehicles((v) => v.map((veh) => ({ ...veh, orders: veh.orders.map((o) => affected.has(o.orderId) ? { ...o, __adjusted: true } : o) })));
  };

  // ── Optimize uygula ────────────────────────────────────────────────────────
  const applyOptimizedOrders = (reordered) => {
    setVehicles((v) => v.map((veh) => vehicleKeyOf(veh) === vehicleKeyOf(optimizeFor) ? { ...veh, orders: reordered } : veh));
  };

  // ── Kaydet / Dönüştür gövdesi ──────────────────────────────────────────────
  const buildSaveRequest = () => {
    const adjByOrder = {};
    Object.entries(adjustments).forEach(([orderDetailId, a]) => {
      (adjByOrder[a.orderId] = adjByOrder[a.orderId] || []).push({ orderDetailId, newQty: a.newQty });
    });
    const totalKm = Object.values(vehicleKm).reduce((sm, k) => sm + (Number(k) || 0), 0);
    return {
      id: planId || undefined,
      vendorId: criteria.vendorId || null,
      warehouseId: criteria.warehouseId || null,
      criteriaJson: JSON.stringify({
        orderDateFrom: criteria.orderDateFrom, orderDateTo: criteria.orderDateTo,
        shipDateFrom: criteria.shipDateFrom, shipDateTo: criteria.shipDateTo,
        customerGroupIds: criteria.customerGroupIds, subGroupIds: criteria.subGroupIds,
        dynamicGroupIds: criteria.dynamicGroupIds, salesRepIds: criteria.salesRepIds,
        additionalFields: criteria.additionalFields, shipmentArea: criteria.shipmentArea,
      }),
      orderDateFrom: fromDateInput(criteria.orderDateFrom),
      // #807 Plana YAZILAN üst sınır da gün sonu olmalı — kolonun anlamı ekrandaki
      // kriterle aynı kalsın (aralık BİTİŞİ dâhil).
      orderDateTo: toDayEndInput(criteria.orderDateTo),
      shipDateFrom: fromDateInput(criteria.shipDateFrom),
      shipDateTo: fromDateInput(criteria.shipDateTo),
      totalKm,
      vehicles: vehicles.map((v, i) => ({
        id: v.id || undefined,
        shipmentVehicleId: v.shipmentVehicleId,
        distributorId: v.distributorId || null,
        sortOrder: i,
        totalKm: Number(vehicleKm[vehicleKeyOf(v)]) || 0,
        totalWeight: v.orders.reduce((sm, o) => sm + (Number(o.totalWeight) || 0), 0),
        totalVolume: v.orders.reduce((sm, o) => sm + (Number(o.totalVolume) || 0), 0),
        routeGeometryJson: null,
        orders: v.orders.map((o, j) => ({
          id: o.id || undefined,
          orderId: o.orderId,
          visitOrderNo: o.visitOrderNo ?? j + 1,
          documentType: o.documentType,
          totalWeight: Number(o.totalWeight) || 0,
          totalVolume: Number(o.totalVolume) || 0,
          adjustmentsJson: adjByOrder[o.orderId] ? JSON.stringify(adjByOrder[o.orderId]) : null,
        })),
      })),
      pendingOrders: bekleyen.map((o) => ({
        orderId: o.orderId, documentType: o.documentType,
        totalWeight: Number(o.totalWeight) || 0, totalVolume: Number(o.totalVolume) || 0,
      })),
      unplannedOrders: [],
    };
  };

  const saveDraft = async () => {
    if (!vehicles.some((v) => v.orders.length) && !bekleyen.length) {
      showToast('error', 'Kaydedilecek sipariş yok. Önce Listele yapın.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/save-draft`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(buildSaveRequest()) });
      const payload = await res.json();
      if (!res.ok) throw new Error(apiMessage(payload, 'Taslak kaydedilemedi'));
      const data = unwrap(payload) || {};
      if (data.id) { setPlanId(data.id); setPlanNo(data.planNo || planNo); setStatus('DRAFT'); navigate(`/shipment-planning/${data.id}`, { replace: true }); }
      showToast('success', `Taslak kaydedildi${data.planNo ? ` (${data.planNo})` : ''}.`);
    } catch (e) {
      showToast('error', e.message || 'Taslak kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const doConvert = async () => {
    setConvertConfirm(false);
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/convert`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(buildSaveRequest()) });
      const payload = await res.json();
      if (!res.ok) throw new Error(apiMessage(payload, 'Dönüştürme başarısız'));
      const data = unwrap(payload) || {};
      const count = (data.converted || []).length;
      setStatus(data.status || 'CONVERTED');
      showToast('success', `${count} belge oluşturuldu. Plan dönüştürüldü.`);
      setTimeout(() => navigate('/shipment-planning'), 1200);
    } catch (e) {
      showToast('error', e.message || 'Dönüştürme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => String(vehicleKeyOf(v)) === String(selectedVehicleId)) || null,
    [vehicles, selectedVehicleId],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedOrder = useMemo(() => (selectedOrderId ? findOrder(selectedOrderId) : null), [selectedOrderId, vehicles, bekleyen]);

  const plannedCount = allPlannedOrderIds.length;
  const totalKm = Object.values(vehicleKm).reduce((sm, k) => sm + (Number(k) || 0), 0);
  const converted = (status || '').toUpperCase() === 'CONVERTED';

  const overloaded = useMemo(
    () => vehicles.filter((v) => {
      const t = vehicleTotals(v);
      return (v.maxWeight > 0 && t.weight > v.maxWeight) || (v.volume > 0 && t.volume > v.volume);
    }),
    [vehicles],
  );
  const canStock = plannedCount > 0;
  const canOptimize = !!selectedVehicle && selectedVehicle.orders.length >= 2;
  // Araç içinde EN AZ 1 sipariş olanların sayısı = "araç sayısı" göstergesi.
  const loadedVehicleCount = useMemo(() => vehicles.filter((v) => v.orders.length > 0).length, [vehicles]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary,#94a3b8)' }}>Plan yükleniyor…</div>;
  }

  return (
    <div style={{ ...s.container, gap: 12, padding: '4px 2px' }}>
      {/* Başlık şeridi */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button style={s.btnSecondary} onClick={() => navigate('/shipment-planning')}>← Geri</button>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, fontFamily: "'Sora','Manrope',sans-serif" }}>
          {isNew && !planId ? 'Yeni Sevkiyat Planı' : `Plan ${planNo || ''}`}
        </h2>
        {status && (() => { const m = planStatusMeta(status); return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>; })()}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 14, fontSize: 12.5, color: 'var(--text-secondary,#64748b)', fontWeight: 700, flexWrap: 'wrap' }}>
          <span>Araç: <b style={{ color: 'var(--accent)' }}>{loadedVehicleCount}</b></span>
          <span>Planlı: <b style={{ color: 'var(--accent)' }}>{plannedCount}</b></span>
          <span>Bekleyen: <b style={{ color: 'var(--warn)' }}>{bekleyen.length}</b></span>
          <span>Toplam KM: <b style={{ color: '#16a34a' }}>{fmtNumber(totalKm, 1)}</b></span>
          {overloaded.length > 0 && <span>Aşırı yük: <b style={{ color: 'var(--danger)' }}>{overloaded.length} araç</b></span>}
        </span>
      </div>

      {/* Kriter çubuğu (3 segment) */}
      <CriteriaBar criteria={criteria} setCriteria={setCriteria} onList={handleList} listing={listing} readOnly={converted} onReset={() => setCriteria(DEFAULT_CRITERIA)} />

      {/* #152 madde 5/11 — Listele'ye basılana kadar alt kısım BOŞ; yol gösteren boş durum. */}
      {!listed ? (
        <div style={{
          marginTop: 6, padding: '40px 24px', textAlign: 'center', borderRadius: 16,
          border: '1.5px dashed var(--border-color,#e5e7eb)', background: 'var(--bg-subtle,#f8fafc)',
          color: 'var(--text-secondary,#64748b)', lineHeight: 1.7,
        }}>
          <div style={{ fontSize: 30, marginBottom: 6 }} aria-hidden="true">🚚</div>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary,#0f172a)' }}>Planlamaya başlamak için filtreleyin</div>
          <div style={{ fontSize: 13 }}>
            <b>Distribütör</b> seçin → sipariş filtrelerini ve <b>planlanacak araçları</b> belirleyin → <b>Listele</b>’ye basın.
            Bekleyen siparişler ve araçlar burada görünecek.
          </div>
        </div>
      ) : (
      <>
      {/* Aksiyon butonları (Haritayı Odakla → Rota Detayı sekmesine taşındı) */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={actBtn('#0073ea', !canStock)} disabled={!canStock} title={canStock ? 'Planlı siparişler için depo stok yeterlilik kontrolü' : 'Önce siparişleri araçlara dağıtın'} onClick={() => setStockOpen(true)}>📦 Stok Kontrol</button>
        <button style={actBtn('#7a3cff', !canOptimize)} disabled={!canOptimize} title={canOptimize ? 'Seçili aracın durak sırasını optimize et' : 'En az 2 duraklı bir araç seçin'} onClick={() => setOptimizeFor(selectedVehicle)}>✨ Rona AI Optimize</button>
        {checkedIds.size > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999,
            background: 'rgba(122,60,255,0.10)', border: '1px solid rgba(122,60,255,0.30)',
            color: '#7a3cff', fontWeight: 800, fontSize: 12.5,
          }}>
            {checkedIds.size} sipariş seçili — birini sürükleyin, hepsi taşınır
            <button type="button" onClick={() => setCheckedIds(new Set())}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontWeight: 800, fontSize: 12.5, textDecoration: 'underline', padding: 0 }}>
              Temizle
            </button>
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {canSave && <button style={{ ...s.btnSecondary, opacity: saving ? 0.6 : 1 }} onClick={saveDraft} disabled={saving || converted}>💾 Taslak Kaydet</button>}
          {canSave && (
            <button style={{ ...s.btnPrimary, opacity: saving || converted ? 0.55 : 1 }} onClick={() => setConvertConfirm(true)} disabled={saving || converted || !plannedCount}>
              ✅ Belge Dönüştür
            </button>
          )}
        </span>
      </div>

      {/* Planlanan Araç seçim çubuğu */}
      {vehicles.length > 0 && (
        <VehicleSelectBar
          vehicles={vehicles}
          vendorId={criteria.vendorId}
          selectedVehicleId={selectedVehicleId}
          onSelect={setSelectedVehicleId}
          onDropToVehicle={onDropToVehicle}
        />
      )}

      {/* 2 panel: Bekleyen | Araç Detayı */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 0.85fr) minmax(420px, 2.15fr)', gap: 12, minHeight: 480 }}>
        <PendingPanel
          orders={bekleyen}
          selectedOrderId={selectedOrderId}
          onSelectOrder={(o) => setSelectedOrderId(o.orderId)}
          onDocTypeChange={changeDocType}
          onDragStart={beginDrag}
          onDragEnd={endDrag}
          onDropHere={onDropToBekleyen}
          checkedIds={checkedIds}
          onToggleChecked={toggleChecked}
          onToggleAll={setCheckedForList}
        />

        <VehicleDetailPanel
          vehicle={selectedVehicle}
          onVendorChange={changeVehicleVendor}
          planVendorId={criteria.vendorId}
          planVendorName={criteria.vendorName}
          selectedOrderId={selectedOrderId}
          onSelectOrder={(o) => setSelectedOrderId(o.orderId)}
          onDocTypeChange={changeDocType}
          onDragStart={beginDrag}
          onDragEnd={endDrag}
          onDropHere={() => selectedVehicle && onDropToVehicle(vehicleKeyOf(selectedVehicle))}
          checkedIds={checkedIds}
          onToggleChecked={toggleChecked}
          onToggleAll={setCheckedForList}
          onRemoveOrder={removeFromVehicle}
          onOptimize={() => { if (selectedVehicle && selectedVehicle.orders.length >= 2) setOptimizeFor(selectedVehicle); }}
          onKmChange={(veh, km) => veh && setVehicleKm((prev) => ({ ...prev, [vehicleKeyOf(veh)]: km }))}
        />
      </div>

      {/* Sipariş Detay (alt) */}
      <div style={{ height: 280 }}>
        <OrderDetailPanel order={selectedOrder} onDocTypeChange={changeDocType} />
      </div>
      </>
      )}

      {stockOpen && (
        <StockControlLayer
          orderIds={allPlannedOrderIds}
          warehouseId={null}
          onClose={() => setStockOpen(false)}
          onApplyAdjustments={applyAdjustments}
          showToast={showToast}
        />
      )}

      {optimizeFor && (
        <OptimizeModal
          vehicle={optimizeFor}
          onClose={() => setOptimizeFor(null)}
          onApply={applyOptimizedOrders}
          showToast={showToast}
        />
      )}

      {convertConfirm && (
        <div style={s.overlay} onClick={() => setConvertConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={s.modalTitle}>Belge Dönüştür</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                <b>{plannedCount}</b> planlı sipariş, seçilen belge türlerine (Fatura/İrsaliye) göre belgeye dönüştürülecek.
                Bu işlem stok hareketi oluşturur. Devam edilsin mi?
              </p>
              {overloaded.length > 0 && (
                <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(226,68,92,0.08)', border: '1px solid rgba(226,68,92,0.3)', color: '#b91c1c', fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}>
                  ⚠️ <b>{overloaded.length} araç</b> kapasitesinin üzerinde yüklü. Yine de devam edebilirsiniz, ancak yükleme planını gözden geçirmeniz önerilir.
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setConvertConfirm(false)}>İptal</button>
              <button style={s.btnPrimary} onClick={doConvert}>Evet, Dönüştür</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

function actBtn(color, disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 12,
    border: `1px solid ${color}40`, background: `${color}12`, color,
    fontWeight: 800, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
    opacity: disabled ? 0.5 : 1,
  };
}

export default ShipmentPlanningBoardPage;
